
import { useState } from 'react';

interface Feature {
  name: string;
  start: boolean | string;
  scale: boolean | string;
  highlight?: boolean;
}

interface Category {
  title: string;
  features: Feature[];
}

const comparisonData: Category[] = [
  {
    title: 'Funcionalidades de Atendimento',
    features: [
      { name: 'Agente de IA baseado em ChatGPT', start: true, scale: true },
      { name: 'Atendimento automático 24/7', start: true, scale: true },
      { name: 'Follow-up automático', start: true, scale: true },
      { name: 'Conversas ativas e personalizadas', start: true, scale: true },
      { name: 'Histórico de atendimento', start: true, scale: true },
      { name: 'Organização de contatos', start: true, scale: true },
    ],
  },
  {
    title: 'Integrações',
    features: [
      { name: 'WhatsApp Business', start: true, scale: true },
      { name: 'Instagram Direct', start: false, scale: true, highlight: true },
      { name: 'Facebook Messenger', start: false, scale: true, highlight: true },
    ],
  },
  {
    title: 'Marketing e Campanhas',
    features: [
      { name: 'Disparos em massa', start: false, scale: true, highlight: true },
      { name: 'Email marketing', start: false, scale: true, highlight: true },
      { name: 'Campanhas automatizadas', start: false, scale: true, highlight: true },
    ],
  },
  {
    title: 'CRM e Gestão',
    features: [
      { name: 'CRM interno', start: 'Básico', scale: 'Avançado', highlight: true },
      { name: 'Dashboard de métricas', start: 'Básico', scale: 'Completo' },
      { name: 'Relatórios de desempenho', start: true, scale: true },
    ],
  },
  {
    title: 'Suporte',
    features: [
      { name: 'Implantação assistida', start: true, scale: true },
      { name: 'Suporte via WhatsApp', start: true, scale: 'Prioritário', highlight: true },
      { name: 'Acompanhamento do CEO', start: false, scale: true, highlight: true },
    ],
  },
];

export default function ComparisonTable() {
  const [expandedCategories, setExpandedCategories] = useState<string[]>(
    comparisonData.map((c) => c.title)
  );

  const toggleCategory = (title: string) => {
    setExpandedCategories((prev) =>
      prev.includes(title) ? prev.filter((t) => t !== title) : [...prev, title]
    );
  };

  const renderValue = (value: boolean | string, isScale: boolean = false) => {
    if (typeof value === 'boolean') {
      return value ? (
        <div className={`w-6 h-6 flex items-center justify-center rounded-full ${isScale ? 'bg-primary/20' : 'bg-accent-green/20'}`}>
          <i className={`ri-check-line ${isScale ? 'text-primary' : 'text-accent-green'}`}></i>
        </div>
      ) : (
        <div className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-100">
          <i className="ri-close-line text-gray-400"></i>
        </div>
      );
    }
    return (
      <span className={`text-sm font-medium ${isScale ? 'text-primary' : 'text-gray-700'}`}>
        {value}
      </span>
    );
  };

  return (
    <section className="py-12 sm:py-16 px-4 sm:px-6">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
            Comparação Detalhada
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Veja todas as funcionalidades lado a lado e escolha o plano que melhor atende suas necessidades
          </p>
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
          <div className="grid grid-cols-3 bg-gray-50 border-b border-gray-200">
            <div className="p-5 font-semibold text-gray-700">Recursos</div>
            <div className="p-5 font-semibold text-gray-700 text-center border-l border-gray-200">
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-star-line text-amber-500"></i>
                </div>
                Start
              </span>
            </div>
            <div className="p-5 font-semibold text-primary text-center border-l border-gray-200 bg-primary/5">
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-rocket-line text-primary"></i>
                </div>
                Scale
              </span>
            </div>
          </div>

          {comparisonData.map((category, catIndex) => (
            <div key={category.title}>
              <div className="grid grid-cols-3 bg-gray-50/50 border-b border-gray-100">
                <div className="col-span-3 p-4 font-semibold text-gray-800 text-sm uppercase tracking-wide">
                  {category.title}
                </div>
              </div>
              {category.features.map((feature, featIndex) => (
                <div
                  key={feature.name}
                  className={`grid grid-cols-3 border-b border-gray-100 hover:bg-gray-50/50 transition-colors ${
                    feature.highlight ? 'bg-primary/[0.02]' : ''
                  }`}
                >
                  <div className={`p-4 text-gray-700 text-sm flex items-center gap-2 ${feature.highlight ? 'border-l-2 border-primary' : ''}`}>
                    {feature.name}
                  </div>
                  <div className="p-4 flex items-center justify-center border-l border-gray-100">
                    {renderValue(feature.start)}
                  </div>
                  <div className="p-4 flex items-center justify-center border-l border-gray-100 bg-primary/[0.02]">
                    {renderValue(feature.scale, true)}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Mobile Accordion */}
        <div className="md:hidden space-y-3">
          {comparisonData.map((category) => (
            <div key={category.title} className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
              <button
                onClick={() => toggleCategory(category.title)}
                className="w-full p-4 flex items-center justify-between bg-gray-50 cursor-pointer"
              >
                <span className="font-semibold text-gray-800 text-sm">{category.title}</span>
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-gray-500 transition-transform ${
                    expandedCategories.includes(category.title) ? 'rotate-180' : ''
                  }`}></i>
                </div>
              </button>
              
              <div className={`overflow-hidden transition-all duration-300 ${
                expandedCategories.includes(category.title) ? 'max-h-[1000px]' : 'max-h-0'
              }`}>
                {category.features.map((feature) => (
                  <div key={feature.name} className={`p-4 border-t border-gray-100 ${feature.highlight ? 'bg-primary/[0.02]' : ''}`}>
                    <p className="text-gray-700 text-sm mb-3">{feature.name}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">Start:</span>
                        {renderValue(feature.start)}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">Scale:</span>
                        {renderValue(feature.scale, true)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
